import React, { Fragment } from "react"
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react"
import { Heading4 } from "./typography/headings"
import { BodyTextRegular } from "./typography/text"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"
import { ButtonTransparent } from "./button/ButtonTransparent"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  isOpen: boolean
  onClose: () => void
  title?: string
  subtitle?: string
  subtitleSpan?: React.ReactNode
  onBackClick?: () => void
  showClose?: boolean
}

// Cannot use DialogBackdrop from headlessui/react: https://github.com/tailwindlabs/headlessui/issues/2876
// Use when next version is released
export const Modal = ({
  isOpen,
  onClose,
  className,
  title,
  subtitle,
  onBackClick,
  children,
  showClose = true,
  subtitleSpan,
  ...rest
}: Props) => {
  return (
    <Transition appear show={isOpen}>
      <Dialog as="div" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={`fixed inset-0 bg-black/20 z-top`} />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed z-top inset-0 flex w-screen items-center justify-center">
            <DialogPanel
              as="div"
              className={`rounded-2xl shadow-modal bg-white max-h-[95%] overflow-y-scroll ${className}`}
              {...rest}
            >
              {(showClose || onBackClick) && (
                <div
                  className={`flex flex-row  items-center ${
                    onBackClick ? "justify-between" : "justify-end"
                  }`}
                >
                  {onBackClick && (
                    <ButtonTransparent
                      size="medium"
                      leftIcon={<FontAwesomeIcon icon={solid("arrow-left")} />}
                      onClick={onBackClick}
                    />
                  )}
                  {showClose && (
                    <ButtonTransparent
                      size="medium"
                      leftIcon={<FontAwesomeIcon icon={solid("xmark")} />}
                      onClick={onClose}
                    />
                  )}
                </div>
              )}
              {title && (
                <div className="flex flex-col gap-2 w-full">
                  <DialogTitle as={Heading4} className="text-textPrimary">
                    {title}
                  </DialogTitle>
                  {(subtitle || subtitleSpan) && (
                    <BodyTextRegular className="text-textSecondary2">
                      {subtitle} {subtitleSpan}
                    </BodyTextRegular>
                  )}
                </div>
              )}
              {children}
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  )
}
